import { useFormContext } from 'react-hook-form';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/ui/form';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { InvitationFormValues } from '@/types/invitation-schema';
import { useUpdateInvitation } from '@/hooks/useInvitations';

interface Props {
  invitationId: string | undefined;
  publicSlug: string | null;
  isPaid?: boolean;
}

const SettingsTab = ({ invitationId, publicSlug, isPaid = false }: Props) => {
  const form = useFormContext<InvitationFormValues>();
  const updateInvitation = useUpdateInvitation();

  // is_published is intentionally NOT routed through autosave. Toggling it is
  // an explicit user action that fires its own mutation; autosave skips this field.
  const handlePublishedChange = (next: boolean) => {
    form.setValue('is_published', next, { shouldDirty: false });
    if (invitationId) {
      updateInvitation.mutate({ id: invitationId, data: { is_published: next } });
    }
  };

  const isPublished = form.watch('is_published');

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Configuración General</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="children_allowed"
            render={({ field }) => (
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Niños permitidos</p>
                  <p className="text-sm text-muted-foreground">
                    Indicar si pueden asistir menores de edad
                  </p>
                </div>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </div>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Publicación</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium">Publicar invitación</p>
              <p className="text-sm text-muted-foreground">
                {!isPaid
                  ? 'Necesitas pagar para publicar. Ve al panel para completar el pago.'
                  : isPublished
                  ? 'Tu invitación está visible para los invitados'
                  : 'Activa para que tus invitados puedan verla'}
              </p>
              {publicSlug && isPaid && (
                <p className="text-xs text-primary mt-1">
                  Link: {window.location.origin}/i/{publicSlug}
                </p>
              )}
            </div>
            {isPaid ? (
              <Switch
                checked={isPublished}
                disabled={updateInvitation.isPending}
                onCheckedChange={handlePublishedChange}
              />
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>
                      <Switch checked={false} disabled aria-label="Publicar (requiere pago)" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Necesitas pagar para publicar</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsTab;
